import { Component, DestroyRef, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  BokkzWageDataService,
  Dienstverband,
  Fiscaal,
  FiscaalHerleidingssituatie,
  FiscaalIncidenteleInkomstenvermindering,
  FiscaalTabelKleur,
  FiscaalWoonlandFiscaal,
  Periode,
  WerknemerEntity,
} from '@snelstart-ventures/fiscaal/domain-logic';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { DetailsConfig, SharedUiDetailsModule } from '@snelstart/ui-details';
import { SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiTabModule } from '@snelstart/ui-tab';
import { SharedUiDialogModule } from '@snelstart/ui-dialog';
import { SharedUiIconModule } from '@snelstart/ui-icon';
import { debounceTime, filter, merge, Observable, shareReplay, Subscription, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type PeriodeFormGroup = FormGroup<{
  begindatum: FormControl<Date | null>;
  einddatum: FormControl<Date | null>;
}>;

type FiscaalFormGroup = FormGroup<{
  periode: PeriodeFormGroup;
  loonheffingskorting: FormControl<boolean | null>;
  anoniemTarief: FormControl<boolean | null>;
  tabelTariefAfwijkend: FormControl<number | null>;
  jaarloonBijzonderTarief: FormControl<number | null>;
  bijzonderTariefAfwijkend: FormControl<number | null>;
  woonlandFiscaal: FormControl<FiscaalWoonlandFiscaal | null>;
  herleidingssituatie: FormControl<FiscaalHerleidingssituatie | null>;
  tabelKleur: FormControl<FiscaalTabelKleur | null>;
  incidenteleInkomstenvermindering: FormControl<FiscaalIncidenteleInkomstenvermindering | null>;
}>;

type LijstFormGroup = FormGroup<{
  fiscaleGegevens: FormArray<FiscaalFormGroup>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-fiscaal',
  standalone: true,
  imports: [
    CommonModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiTabModule,
    SharedUiDialogModule,
    SharedUiIconModule
  ],
  templateUrl: './fiscaal.component.html',
  styleUrl: './fiscaal.component.scss'
})
export class FiscaalComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  @Input() selectedTabDv = 0;
  private readonly destroyRef = inject(DestroyRef);
  private readonly dataService = inject(BokkzWageDataService);
  private werknemerEntity = {} as WerknemerEntity;
  private recalculationSubscription?: Subscription;

  protected detailsConfig = DetailsConfig.create({
    hideArrowButtons: false
  });

  public lijstFg: LijstFormGroup = new FormGroup({
    fiscaleGegevens: new FormArray<FiscaalFormGroup>([])
  });

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    { value: true, label: 'Ja' },
    { value: false, label: 'Nee' }
  ];

  readonly werknemerEntity$: Observable<WerknemerEntity> =
    this.dataService.werknemerEntity$.pipe(
      filter((data): data is WerknemerEntity => !!data?.id),
      tap((data) => {
        this.werknemerEntity = data;
        if (data.werknemerGegevens?.dienstverbanden?.at(0)) {
          this.updateFormFiscaal(data.werknemerGegevens.dienstverbanden[0].fiscaleGegevens ?? []);
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({ bufferSize: 1, refCount: true })
    );

  ngOnInit(): void {
    this.bindRecalculationHandlers();
  }

  ngOnDestroy(): void {
    this.saveForm();
    this.recalculationSubscription?.unsubscribe();
  }

  get fiscaleGegevens(): FormArray<FiscaalFormGroup> {
    return this.lijstFg.controls.fiscaleGegevens;
  }

  addRij(): void {
    const length = this.fiscaleGegevens.length;
    let newDate = new Date();
    if (length > 0) {
      const lastRow = this.fiscaleGegevens.at(length - 1);
      const laatsteEinddatum = new Date(lastRow.controls.periode.controls.einddatum.value!);
      newDate = new Date(laatsteEinddatum);
      newDate.setDate(laatsteEinddatum.getDate() + 1);
    }
    const nieuweFiscaleGegevens = {
      periode: {
        begindatum: newDate
      },
      loonheffingskorting: this.fiscaleGegevens.at(length - 1).controls.loonheffingskorting.value,
      anoniemTarief: this.fiscaleGegevens.at(length - 1).controls.anoniemTarief.value,
      tabelTariefAfwijkend: this.fiscaleGegevens.at(length - 1).controls.tabelTariefAfwijkend.value,
      jaarloonBijzonderTarief: this.fiscaleGegevens.at(length - 1).controls.jaarloonBijzonderTarief.value,
      bijzonderTariefAfwijkend: this.fiscaleGegevens.at(length - 1).controls.bijzonderTariefAfwijkend.value,
      woonlandFiscaal: this.fiscaleGegevens.at(length - 1).controls.woonlandFiscaal.value,
      herleidingssituatie: this.fiscaleGegevens.at(length - 1).controls.herleidingssituatie.value,
      tabelKleur: this.fiscaleGegevens.at(length - 1).controls.tabelKleur.value,
      incidenteleInkomstenvermindering: this.fiscaleGegevens.at(length - 1).controls.incidenteleInkomstenvermindering.value,
    } as Fiscaal;
    this.fiscaleGegevens.push(this.createFormGroupFiscaal(nieuweFiscaleGegevens));
    this.saveForm();
    this.bindRecalculationHandlers();
  }

  verwijderRijFiscaal(index: number): void {
    if (index > 0) {
      this.fiscaleGegevens.at(index - 1).controls.periode.patchValue({ einddatum: undefined });
    }
    this.fiscaleGegevens.removeAt(index);
    this.fiscaleGegevens.updateValueAndValidity();
    this.saveForm();
    this.bindRecalculationHandlers();
  }

  private createFormGroupFiscaal(item?: Fiscaal): FiscaalFormGroup {
    return new FormGroup({
      periode: this.createPeriodeFormGroup(item?.periode),
      loonheffingskorting: new FormControl<boolean | null>(item?.loonheffingskorting ?? true),
      anoniemTarief: new FormControl<boolean | null>(item?.anoniemTarief ?? false),
      tabelTariefAfwijkend: new FormControl<number | null>(item?.tabelTariefAfwijkend ?? null),
      jaarloonBijzonderTarief: new FormControl<number | null>(item?.jaarloonBijzonderTarief ?? null),
      bijzonderTariefAfwijkend: new FormControl<number | null>(item?.bijzonderTariefAfwijkend ?? null),
      woonlandFiscaal: new FormControl<FiscaalWoonlandFiscaal | null>(
        item?.woonlandFiscaal ?? FiscaalWoonlandFiscaal.NEDERLAND
      ),
      herleidingssituatie: new FormControl<FiscaalHerleidingssituatie | null>(
        item?.herleidingssituatie ?? FiscaalHerleidingssituatie.G
      ),
      tabelKleur: new FormControl<FiscaalTabelKleur | null>(item?.tabelKleur ?? FiscaalTabelKleur.WIT),
      incidenteleInkomstenvermindering: new FormControl<FiscaalIncidenteleInkomstenvermindering | null>(
        item?.incidenteleInkomstenvermindering ?? null
      ),
    });
  }

  private createPeriodeFormGroup(periode?: Periode): PeriodeFormGroup {
    return new FormGroup({
      begindatum: new FormControl<Date | null>(periode?.begindatum ?? null),
      einddatum: new FormControl<Date | null>(periode?.einddatum ?? null),
    });
  }

  private updateFormFiscaal(fiscaleGegevens: Fiscaal[]): void {
    const lijst = fiscaleGegevens || [];
    const formArray = this.fiscaleGegevens;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1);
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        formArray.at(i).patchValue(lijst[i], { emitEvent: false });
      } else {
        formArray.push(this.createFormGroupFiscaal(lijst[i]));
      }
    }

    this.bindRecalculationHandlers();
  }

  private bindRecalculationHandlers(): void {
    this.recalculationSubscription?.unsubscribe();

    const loonheffingskortingStreams = this.fiscaleGegevens.controls.map(control =>
      control.controls.loonheffingskorting.valueChanges
    );
    const jaarloonBijzonderTariefStreams = this.fiscaleGegevens.controls.map(control =>
      control.controls.jaarloonBijzonderTarief.valueChanges
    );

    const recalcStreams = [...loonheffingskortingStreams, ...jaarloonBijzonderTariefStreams];
    if (!recalcStreams.length) {
      this.recalculationSubscription = undefined;
      return;
    }

    this.recalculationSubscription = merge(...recalcStreams)
      .pipe(debounceTime(200), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.saveForm();
        this.dataService
          .calculateLatestWerknemerEntity()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe();
      });
  }

  public checkEinddatum(): boolean {
    const length = this.fiscaleGegevens.length;
    const waarde = this.fiscaleGegevens.at(length - 1).controls.periode.controls.einddatum.value;
    return !!waarde;
  }

  public saveForm(): void {
    const fiscaleGegevens = this.lijstFg.getRawValue().fiscaleGegevens;

    this.dataService.patchWerknemerEntity((current) => {
      const baseEntity = current?.werknemerGegevens ? current : this.werknemerEntity;
      const eersteDienstverband = baseEntity.werknemerGegevens?.dienstverbanden?.at(0);

      if (!baseEntity.werknemerGegevens || !eersteDienstverband) {
        return baseEntity;
      }

      const dienstverband = {
        ...eersteDienstverband,
        dga: false,
        fiscaleGegevens,
      } as Dienstverband;

      const nextWerknemer = {
        ...baseEntity,
        werknemerGegevens: {
          ...baseEntity.werknemerGegevens,
          dienstverbanden: [dienstverband]
        }
      } as WerknemerEntity;

      this.werknemerEntity = nextWerknemer;
      return nextWerknemer;
    });
  }
}
